import { X, Send, Image, MessageCircle, Reply, Trash2, ThumbsUp, Heart, Laugh, ThumbsDown, Download, FileText, CheckCircle, Circle, ArrowRight, Clock, Shield, AlertTriangle, Zap, TrendingUp } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import config from "../../config";

const REACTION_TYPES = {
  Like: { icon: ThumbsUp, color: "text-blue-400", hoverColor: "hover:bg-blue-500/20" },
  Love: { icon: Heart, color: "text-red-400", hoverColor: "hover:bg-red-500/20" },
  Laugh: { icon: Laugh, color: "text-yellow-400", hoverColor: "hover:bg-yellow-500/20" },
  Dislike: { icon: ThumbsDown, color: "text-slate-400", hoverColor: "hover:bg-slate-500/20" },
};

// ─── SLA Section Component ────────────────────────────────────────────────────
function SLASection({ ticket, currentStatus, apiBaseUrl }) {
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const fetchSLA = async () => {
      try {
        setLoading(true);
        // Fetch SLA by priority endpoint — returns array, grab first item
        const res = await fetch(`${apiBaseUrl}/SLA/priority/${encodeURIComponent(ticket.priority)}`);
        if (res.ok) {
          const data = await res.json();
          const sla = Array.isArray(data) ? data[0] : data;
          setSlaData(sla || null);
        } else {
          setSlaData(null);
        }
      } catch {
        setSlaData(null);
      } finally {
        setLoading(false);
      }
    };
    if (ticket.priority) fetchSLA();
    else setLoading(false);
  }, [ticket.priority, apiBaseUrl]);

  // Live elapsed time ticker (updates every minute)
  useEffect(() => {
    const calcElapsed = () => {
      if (!ticket.createdAt) return 0;
      const created = new Date(ticket.createdAt);
      const now = new Date();
      return Math.floor((now - created) / 1000 / 60); // minutes
    };
    setElapsed(calcElapsed());
    const interval = setInterval(() => setElapsed(calcElapsed()), 60000);
    return () => clearInterval(interval);
  }, [ticket.createdAt]);

  const parseTimeGoalToMinutes = (timeGoal) => {
    if (!timeGoal) return null;
    const tg = timeGoal.toLowerCase().trim();
    const match = tg.match(/^(\d+(?:\.\d+)?)\s*(h|hr|hours?|d|days?|w|weeks?|m|min|minutes?)$/);
    if (!match) return null;
    const val = parseFloat(match[1]);
    const unit = match[2];
    if (unit.startsWith("h")) return val * 60;
    if (unit.startsWith("d")) return val * 60 * 24;
    if (unit.startsWith("w")) return val * 60 * 24 * 7;
    if (unit.startsWith("m")) return val;
    return null;
  };

  const formatMinutes = (mins) => {
    if (mins < 60) return `${Math.round(mins)}m`;
    if (mins < 1440) return `${Math.round(mins / 60)}h`;
    return `${Math.round(mins / 1440)}d`;
  };

  const goalMinutes = slaData ? parseTimeGoalToMinutes(slaData.timeGoal) : null;
  const isResolved = currentStatus === "Resolved";
  const progressPct = goalMinutes ? Math.min((elapsed / goalMinutes) * 100, 100) : 0;
  const remaining = goalMinutes ? goalMinutes - elapsed : null;
  const isBreached = goalMinutes ? elapsed > goalMinutes && !isResolved : false;
  const isAtRisk = goalMinutes ? !isBreached && remaining !== null && remaining < goalMinutes * 0.2 && !isResolved : false;

  const slaStatus = isResolved
    ? "resolved"
    : isBreached
      ? "breached"
      : isAtRisk
        ? "at-risk"
        : "on-track";

  const statusConfig = {
    "on-track": {
      label: "On Track",
      icon: Shield,
      gradient: "from-emerald-500/20 to-teal-500/10",
      border: "border-emerald-500/30",
      iconColor: "text-emerald-400",
      labelColor: "text-emerald-400",
      barColor: "from-emerald-500 to-teal-400",
      glow: "shadow-emerald-500/20",
      pulse: false,
    },
    "at-risk": {
      label: "At Risk",
      icon: AlertTriangle,
      gradient: "from-amber-500/20 to-orange-500/10",
      border: "border-amber-500/30",
      iconColor: "text-amber-400",
      labelColor: "text-amber-400",
      barColor: "from-amber-500 to-orange-400",
      glow: "shadow-amber-500/20",
      pulse: true,
    },
    breached: {
      label: "SLA Breached",
      icon: Zap,
      gradient: "from-red-500/20 to-rose-500/10",
      border: "border-red-500/30",
      iconColor: "text-red-400",
      labelColor: "text-red-400",
      barColor: "from-red-500 to-rose-400",
      glow: "shadow-red-500/20",
      pulse: true,
    },
    resolved: {
      label: "Completed",
      icon: CheckCircle,
      gradient: "from-blue-500/20 to-indigo-500/10",
      border: "border-blue-500/30",
      iconColor: "text-blue-400",
      labelColor: "text-blue-400",
      barColor: "from-blue-500 to-indigo-400",
      glow: "shadow-blue-500/20",
      pulse: false,
    },
  };

  const cfg = statusConfig[slaStatus];
  const StatusIcon = cfg.icon;

  if (loading) {
    return (
      <div>
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p>
        <div className="flex items-center justify-center py-4">
          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!slaData) {
    return (
      <div>
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 text-center">
          <Shield size={20} className="text-slate-600 mx-auto mb-1.5" />
          <p className="text-slate-500 text-xs">No SLA configured for <span className="text-slate-400 font-medium">{ticket.priority || "this priority"}</span></p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">SLA</p>

      {/* Main SLA Card */}
      <div className={`bg-gradient-to-br ${cfg.gradient} border ${cfg.border} rounded-xl p-3.5 shadow-lg ${cfg.glow} mb-2`}>
        {/* Status Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-black/20 ${cfg.pulse ? "animate-pulse" : ""}`}>
              <StatusIcon size={14} className={cfg.iconColor} />
            </div>
            <span className={`text-xs font-bold ${cfg.labelColor}`}>{cfg.label}</span>
          </div>
          <span className="text-slate-400 text-xs font-mono bg-black/20 px-2 py-0.5 rounded-md">
            {slaData.timeGoal}
          </span>
        </div>

        {/* Progress Bar */}
        {goalMinutes && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-slate-400 text-xs">
                {isResolved ? "Completed in" : "Elapsed"}
              </span>
              <span className="text-slate-300 text-xs font-mono font-semibold">
                {formatMinutes(elapsed)} / {slaData.timeGoal}
              </span>
            </div>
            <div className="h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${cfg.barColor} transition-all duration-700`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Time Remaining / Overdue */}
        {goalMinutes && !isResolved && (
          <div className={`flex items-center gap-2 rounded-lg p-2 ${isBreached ? "bg-red-500/10" : "bg-black/20"}`}>
            <Clock size={12} className={isBreached ? "text-red-400" : "text-slate-400"} />
            <span className={`text-xs font-medium ${isBreached ? "text-red-400" : "text-slate-300"}`}>
              {isBreached
                ? `Overdue by ${formatMinutes(Math.abs(remaining))}`
                : `${formatMinutes(remaining)} remaining`}
            </span>
          </div>
        )}
      </div>

      {/* SLA Meta Info */}
      <div className="space-y-1.5">
        {slaData.calendar && (
          <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700/40 rounded-lg px-3 py-2">
            <span className="text-slate-500 text-xs">Calendar</span>
            <span className="text-slate-300 text-xs font-medium">{slaData.calendar}</span>
          </div>
        )}
        {slaData.condition && (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg px-3 py-2">
            <p className="text-slate-500 text-xs mb-1">Condition</p>
            <p className="text-slate-300 text-xs leading-relaxed line-clamp-2">{slaData.condition}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main TicketModal ────────────────────────────────────────────────────────

export default function TicketModal({ ticket, onClose, formatDate, getPriorityColor, apiBaseUrl = config.API_BASE_URL, onStatusChange }) {
  const [activities, setActivities] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [profilePictureCache, setProfilePictureCache] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const imageInputRef = useRef(null);

  const API_BASE = apiBaseUrl;

  const getCurrentUser = () => {
    try {
      const adminData = localStorage.getItem("admin");
      const userData = localStorage.getItem("user");
      if (adminData) {
        const admin = JSON.parse(adminData);
        return { userId: admin.username || admin.email || "admin", userName: admin.fullNames || admin.username || "Admin User" };
      } else if (userData) {
        const user = JSON.parse(userData);
        return { userId: user.username || user.email || "user", userName: user.fullNames || user.username || "User" };
      }
      return { userId: "anonymous", userName: "Anonymous User" };
    } catch { return { userId: "anonymous", userName: "Anonymous User" }; }
  };
  const [userProfilePic, setUserProfilePic] = useState(null);
  useEffect(() => {
    const fetchUserPic = async () => {
      if (!ticket.email) return;
      try {
        const res = await fetch(`${API_BASE}/Users/get-profile-picture/${encodeURIComponent(ticket.email)}`);
        if (res.ok) {
          const blob = await res.blob();
          setUserProfilePic(URL.createObjectURL(blob));
        }
        // silent if no pic — no error log
      } catch {
        // silent
      }
    };
    fetchUserPic();
  }, [ticket.email]);
  const currentUser = getCurrentUser();

  const fetchUserProfilePicture = async (userId) => {
    if (profilePictureCache[userId]) return profilePictureCache[userId];
    try {
      const response = await fetch(`${API_BASE}/Admin/get-profile-picture/${userId}`);
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setProfilePictureCache(prev => ({ ...prev, [userId]: imageUrl }));
        return imageUrl;
      }
      return null;
    } catch { return null; }
  };

  const getUserAvatar = (userName, profilePicture, size = "w-full h-full") => {
    if (profilePicture) return <img src={profilePicture} alt={userName} className={`${size} object-cover`} />;
    return <span className="text-white font-bold text-sm">{userName.charAt(0).toUpperCase()}</span>;
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
  });

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const clearAttachments = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  useEffect(() => { fetchActivities(); }, [ticket.ticketNumber]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/Activities/ticket/${encodeURIComponent(ticket.ticketNumber)}?userId=${currentUser.userId}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const activitiesWithPics = await Promise.all(
          data.map(async (activity) => {
            const profilePicture = await fetchUserProfilePicture(activity.userId);
            const commentsWithPics = await Promise.all(
              (activity.comments || []).map(async (comment) => {
                const commentProfilePic = await fetchUserProfilePicture(comment.userId);
                const repliesWithPics = await Promise.all(
                  (comment.replies || []).map(async (reply) => {
                    const replyProfilePic = await fetchUserProfilePicture(reply.userId);
                    return { ...reply, profilePicture: replyProfilePic };
                  })
                );
                return { ...comment, profilePicture: commentProfilePic, replies: repliesWithPics };
              })
            );
            return { ...activity, profilePicture, comments: commentsWithPics };
          })
        );
        setActivities(activitiesWithPics);
      }
    } catch (error) { console.error("Error fetching activities:", error); }
    finally { setLoading(false); }
  };

  const handlePostActivity = async () => {
    if (!newPost.trim() && !selectedImage) { alert("Please add content or image before posting."); return; }
    try {
      const payload = {
        ticketNumber: ticket.ticketNumber,
        userId: currentUser.userId,
        userName: currentUser.userName,
        content: newPost.trim() || " ",
      };
      if (selectedImage) { payload.imageBase64 = await fileToBase64(selectedImage); }
      const response = await fetch(`${API_BASE}/Activities`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (response.ok) { setNewPost(""); clearAttachments(); fetchActivities(); }
      else { const errorText = await response.text(); alert(`Failed to post: ${errorText}`); }
    } catch (error) { alert("Error posting activity."); }
  };

  const handlePostComment = async (activityId, parentCommentId = null) => {
    if (!newPost.trim()) return;
    try {
      const payload = { activityId, userId: currentUser.userId, userName: currentUser.userName, content: newPost };
      if (parentCommentId) payload.parentCommentId = parentCommentId;
      const response = await fetch(`${API_BASE}/Activities/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (response.ok) { setNewPost(""); setReplyingTo(null); fetchActivities(); }
    } catch (error) { console.error("Error posting comment:", error); }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const response = await fetch(`${API_BASE}/Activities/${activityId}`, { method: "DELETE" });
      if (response.ok) fetchActivities();
    } catch (error) { console.error("Error deleting activity:", error); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await fetch(`${API_BASE}/Activities/comments/${commentId}`, { method: "DELETE" });
      if (response.ok) fetchActivities();
    } catch (error) { console.error("Error deleting comment:", error); }
  };

  const handleActivityReaction = async (activityId, reactionType) => {
    try {
      const activity = activities.find(a => a.activityId === activityId);
      const currentReaction = activity?.reactionSummary?.userReaction;
      if (currentReaction === reactionType) {
        const r = await fetch(`${API_BASE}/Activities/${activityId}/reactions/${currentUser.userId}`, { method: "DELETE" });
        if (r.ok) fetchActivities();
      } else {
        const r = await fetch(`${API_BASE}/Activities/${activityId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.userId, userName: currentUser.userName, reactionType }) });
        if (r.ok) fetchActivities();
      }
    } catch (error) { console.error("Error handling reaction:", error); }
  };

  const handleCommentReaction = async (commentId, reactionType) => {
    try {
      let currentReaction = null;
      for (const activity of activities) {
        const comment = activity.comments?.find(c => c.commentId === commentId);
        if (comment) { currentReaction = comment.reactionSummary?.userReaction; break; }
        for (const c of activity.comments || []) {
          const reply = c.replies?.find(r => r.commentId === commentId);
          if (reply) { currentReaction = reply.reactionSummary?.userReaction; break; }
        }
      }
      if (currentReaction === reactionType) {
        const r = await fetch(`${API_BASE}/Activities/comments/${commentId}/reactions/${currentUser.userId}`, { method: "DELETE" });
        if (r.ok) fetchActivities();
      } else {
        const r = await fetch(`${API_BASE}/Activities/comments/${commentId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.userId, userName: currentUser.userName, reactionType }) });
        if (r.ok) fetchActivities();
      }
    } catch (error) { console.error("Error handling comment reaction:", error); }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (currentStatus === newStatus) return;
    try {
      setUpdatingStatus(true);
      const updatedTicket = { ...ticket, status: newStatus };
      const response = await fetch(`${API_BASE}/Tickets/${ticket.ticketNumber}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedTicket),
      });
      if (response.ok) {
        setCurrentStatus(newStatus);
        if (onStatusChange) onStatusChange(ticket.ticketNumber, newStatus);

        // ── FIRE-AND-FORGET EMAIL ─────────────────────────────────────────────
        if (ticket.email) {
          const statusMessages = {
            "In Progress": { headline: "Your ticket is now being worked on!", detail: "A support agent has picked up your ticket and is actively working on it." },
            "Resolved": { headline: "Your ticket has been resolved!", detail: "Our support team has resolved your issue. If you still have concerns, feel free to submit a new ticket." },
            "Closed": { headline: "Your ticket has been closed.", detail: "This ticket has been closed. Thank you for reaching out! If you need further help, submit a new ticket anytime." },
            "Pending": { headline: "Your ticket is pending review.", detail: "Your ticket is currently queued and waiting to be assigned to a support agent." },
          };

          const statusInfo = statusMessages[newStatus];

          if (statusInfo) {
            const payload = {
              toEmail: ticket.email,
              toName: ticket.name || "User",
              subject: `[Ticket Update] ${ticket.ticketNumber} - Status changed to ${newStatus}`,
              body:
                `Hi ${ticket.name || "User"},\n\n` +
                `${statusInfo.headline}\n\n` +
                `${statusInfo.detail}\n\n` +
                `TICKET DETAILS\n` +
                `Ticket Number  : ${ticket.ticketNumber}\n` +
                `Subject        : ${ticket.subject || "N/A"}\n` +
                `Category       : ${ticket.category || "N/A"}\n` +
                `Priority       : ${ticket.priority || "N/A"}\n` +
                `Previous Status: ${currentStatus}\n` +
                `New Status     : ${newStatus}\n` +
                `Updated On     : ${new Date().toLocaleString()}\n\n` +
                `You can log in to the User Portal to view your ticket and track further updates.\n\n` +
                `Best regards,\n` +
                `Orca Helpdesk Team\n\n` +
                `DO NOT REPLY TO THIS EMAIL.\n` +
                `This is an automated message. Replies to this email will not be monitored or received.\n` +
                `If you have concerns, please log in to the User Portal instead.`,
            };

            console.log("📧 Sending email payload:", payload);

            // no await — fire and forget
            fetch(`${API_BASE}/Email/send`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            })
              .then(async r => {
                const data = await r.json().catch(() => ({}));
                if (r.ok) {
                  console.log(
                    `%cEmail sent%c -> %c${ticket.email}%c [${currentStatus} -> ${newStatus}]`,
                    "color: #4ade80; font-weight: bold",
                    "color: #94a3b8",
                    "color: #f472b6; font-weight: bold",
                    "color: #94a3b8"
                  );
                } else {
                  console.warn(`Email API returned ${r.status}:`, data);
                }
              })
              .catch(err => console.warn(`Email failed for ${ticket.ticketNumber}:`, err.message));
          }
        } else {
          console.warn(`No email found for ticket ${ticket.ticketNumber} — skipped`);
        }
        // ─────────────────────────────────────────────────────────────────────

      } else {
        const err = await response.text();
        alert(`Failed to update status: ${err}`);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getNextStatus = (status) => {
    if (status === "Pending") return "In Progress";
    if (status === "In Progress") return "Resolved";
    return null;
  };

  const getNextStatusLabel = (status) => {
    if (status === "Pending") return { label: "Move to In Progress", icon: Clock, color: "bg-amber-500 hover:bg-amber-600", iconColor: "text-amber-200" };
    if (status === "In Progress") return { label: "Mark as Resolved", icon: CheckCircle, color: "bg-green-500 hover:bg-green-600", iconColor: "text-green-200" };
    return null;
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
      "In Progress": "bg-blue-500/15 text-blue-400 border-blue-500/30",
      Resolved: "bg-green-500/15 text-green-400 border-green-500/30",
    };
    return colors[status] || "bg-slate-500/15 text-slate-400 border-slate-500/30";
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      Low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      Medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      High: "bg-red-500/15 text-red-400 border-red-500/30",
      Critical: "bg-red-600/20 text-red-300 border-red-600/40",
    };
    return colors[priority] || "bg-slate-500/15 text-slate-400 border-slate-500/30";
  };

  const nextAction = getNextStatusLabel(currentStatus);
  const nextStatus = getNextStatus(currentStatus);

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col rounded-2xl shadow-2xl"
        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(148,163,184,0.12)" }}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50" style={{ background: "linear-gradient(90deg, hsl(224, 76%, 48%) 0%, hsl(224, 76%, 48%))" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <MessageCircle size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">{ticket.ticketNumber}</h2>
              <p className="text-blue-200 text-xs">{formatDate ? formatDate(ticket.createdAt) : ticket.createdAt}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(currentStatus)}`}>{currentStatus}</span>
            <span className={`px-3 py-1 rounded-full border text-xs font-medium ${getPriorityBadge(ticket.priority)}`}>{ticket.priority || "N/A"}</span>
            <button onClick={onClose} className="ml-2 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <X size={16} className="text-white" />
            </button>
          </div>
        </div>

        {/* 3-COLUMN BODY */}
        <div className="flex-1 overflow-hidden flex">
          {/* LEFT: Ticket Details */}
          <div className="w-72 flex-shrink-0 border-r border-slate-700/50 overflow-y-auto p-5 space-y-5">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Ticket Details</p>

              <div className="mb-3">
                <p className="text-slate-500 text-xs mb-1">Subject</p>
                <p className="text-white text-sm font-semibold">{ticket.subject || ticket.title || "—"}</p>
              </div>

              <div className="space-y-2.5">
                <DetailRow label="Ticket ID" value={ticket.ticketNumber} />
                <DetailRow label="Category" value={ticket.category} />
                <DetailRow label="Priority" value={ticket.priority} highlight={getPriorityBadge(ticket.priority)} />
                <DetailRow label="Status" value={currentStatus} highlight={getStatusColor(currentStatus)} />
                <DetailRow label="Submitted" value={formatDate ? formatDate(ticket.createdAt) : ticket.createdAt} />
              </div>
            </div>

            {/* Status Progression */}
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Update Status</p>

              <div className="flex items-center gap-1 mb-3">
                {[
                  { label: "Pending", color: "bg-yellow-500" },
                  { label: "In Progress", color: "bg-blue-500" },
                  { label: "Resolved", color: "bg-green-500" },
                ].map((step, i, arr) => (
                  <div key={step.label} className="flex items-center gap-1 flex-1">
                    <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${currentStatus === step.label
                      ? step.color
                      : (i === 0 && (currentStatus === "In Progress" || currentStatus === "Resolved")) || (i === 1 && currentStatus === "Resolved")
                        ? step.color + " opacity-70"
                        : "bg-slate-700"
                      }`} />
                    {i < arr.length - 1 && <ArrowRight size={8} className="text-slate-600 flex-shrink-0" />}
                  </div>
                ))}
              </div>

              {nextAction && nextStatus ? (
                <button
                  onClick={() => handleStatusUpdate(nextStatus)}
                  disabled={updatingStatus}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-white text-xs font-semibold transition-all duration-200 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed ${nextAction.color}`}
                >
                  {updatingStatus ? (
                    <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <nextAction.icon size={14} className={nextAction.iconColor} />
                  )}
                  {updatingStatus ? "Updating..." : nextAction.label}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <CheckCircle size={14} className="text-green-400" />
                  <span className="text-green-400 text-xs font-semibold">Ticket Resolved</span>
                </div>
              )}

              <div className="flex gap-1.5 mt-2">
                {[
                  { status: "Pending", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20" },
                  { status: "In Progress", color: "bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20" },
                  { status: "Resolved", color: "bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20" },
                ].map(({ status, color }) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    disabled={updatingStatus || currentStatus === status}
                    className={`flex-1 px-1.5 py-1.5 rounded-lg border text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${color} ${currentStatus === status ? "ring-1 ring-offset-1 ring-offset-slate-900" : ""}`}
                    title={`Set to ${status}`}
                  >
                    {status === "Pending" ? "Pending" : status === "In Progress" ? "In Prog." : "Done"}
                  </button>
                ))}
              </div>
            </div>

            {ticket.attachment && (
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-widest mb-2 font-semibold">Attachment</p>
                <div className="bg-slate-800/60 rounded-lg p-2.5 border border-slate-700/50 flex items-center gap-2">
                  <FileText size={14} className="text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300 text-xs truncate">{ticket.attachment}</span>
                </div>
              </div>
            )}

            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Submitted By</p>
              <div className="bg-slate-800/40 rounded-xl p-3 border border-slate-700/40">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {userProfilePic
                        ? <img src={userProfilePic} alt="user" className="w-full h-full object-cover" />
                        : <span className="text-white font-bold text-sm">
                          {(ticket.submittedBy || ticket.requesterName || "U").charAt(0).toUpperCase()}
                        </span>
                      }
                    </div>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-tight">{ticket.name || "—"}</p>
                    <p className="text-slate-400 text-xs">{ticket.email || "—"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-900/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">Year Level</p>
                    <p className="text-slate-200 text-xs font-medium mt-0.5">{ticket.yearLevel || "—"}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">Section</p>
                    <p className="text-slate-200 text-xs font-medium mt-0.5">{ticket.section || "—"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER: Activity Feed */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700/50 bg-slate-900/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Progress</span>
                <span className="text-blue-400 text-xs font-bold">
                  {currentStatus === "Resolved" ? "100%" : currentStatus === "In Progress" ? "50%" : "10%"}
                </span>
              </div>
              <div className="w-full bg-slate-700/50 rounded-full h-1.5 mb-3">
                <div
                  className="h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: currentStatus === "Resolved" ? "100%" : currentStatus === "In Progress" ? "50%" : "10%",
                    background: "linear-gradient(90deg, #3b82f6, #06b6d4)"
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                {[
                  { label: "Submitted", done: true },
                  { label: "In Review", done: currentStatus === "In Progress" || currentStatus === "Resolved" },
                  { label: "Resolved", done: currentStatus === "Resolved" },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${step.done ? "bg-blue-500" : "bg-slate-700"}`}>
                      {step.done && <CheckCircle size={10} className="text-white" />}
                    </div>
                    <span className={`text-xs ${step.done ? "text-blue-400 font-medium" : "text-slate-500"}`}>{step.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-4">Activity Feed</p>

              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">No activities yet. Be the first to post!</div>
              ) : (
                activities.map((activity) => (
                  <ActivityPost
                    key={activity.activityId}
                    activity={activity}
                    onDelete={handleDeleteActivity}
                    onReply={(activityId, commentId, userName) => setReplyingTo({ activityId, commentId, userName })}
                    onPostComment={handlePostComment}
                    onDeleteComment={handleDeleteComment}
                    onActivityReaction={handleActivityReaction}
                    onCommentReaction={handleCommentReaction}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    newPost={newPost}
                    setNewPost={setNewPost}
                    formatDate={formatDate}
                    getUserAvatar={getUserAvatar}
                    currentUser={currentUser}
                    API_BASE={API_BASE}
                  />
                ))
              )}
            </div>

            {!replyingTo && (
              <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/20">
                {imagePreview && (
                  <div className="mb-2 relative inline-block">
                    <img src={imagePreview} alt="Preview" className="max-h-28 rounded-lg border border-slate-700" />
                    <button onClick={clearAttachments} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"><X size={12} /></button>
                  </div>
                )}
                <div className="flex items-center gap-3 bg-slate-800/60 rounded-xl border border-slate-700/50 px-4 py-2.5">
                  <input
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handlePostActivity(); } }}
                    placeholder="Add a comment..."
                    className="flex-1 bg-transparent text-white placeholder-slate-500 text-sm outline-none"
                  />
                  <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                  <button onClick={() => imageInputRef.current?.click()} className="text-slate-400 hover:text-blue-400 transition-colors p-1"><Image size={16} /></button>
                  <button
                    onClick={handlePostActivity}
                    disabled={!newPost.trim() && !selectedImage}
                    className="w-8 h-8 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 flex items-center justify-center transition-colors"
                  >
                    <Send size={14} className="text-white" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: SLA + Assigned To + Quick Stats */}
          <div className="w-60 flex-shrink-0 border-l border-slate-700/50 overflow-y-auto p-5 space-y-5">

            {/* ─── SLA SECTION ─── */}
            <SLASection
              ticket={ticket}
              currentStatus={currentStatus}
              apiBaseUrl={API_BASE}
            />

            {/* Divider */}
            <div className="border-t border-slate-700/50" />

            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Assigned To</p>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-300 font-bold text-sm">{(ticket.assignees || "U").charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{ticket.assignees || "Unassigned"}</p>
                  <p className="text-slate-500 text-xs">Assignee</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Quick Stats</p>
              <div className="space-y-2">
                <StatRow label="Days Open" value={ticket.daysOpen || "—"} valueColor="text-blue-400" />
                <StatRow label="Priority" value={ticket.priority || "—"} valueColor={
                  ticket.priority === "Low" ? "text-emerald-400" :
                    ticket.priority === "Medium" ? "text-amber-400" :
                      ticket.priority === "High" || ticket.priority === "Critical" ? "text-red-400" :
                        "text-slate-300"
                } />
                <StatRow label="Stage" value={currentStatus || "—"} valueColor={
                  currentStatus === "Pending" ? "text-yellow-400" :
                    currentStatus === "In Progress" ? "text-blue-400" :
                      currentStatus === "Resolved" ? "text-green-400" : "text-slate-300"
                } />
              </div>
            </div>

            <div>
              <p className="text-slate-500 text-xs uppercase tracking-widest mb-3 font-semibold">Labels</p>
              <div className="flex flex-wrap gap-1.5">
                {ticket.category && (
                  <span className="px-2 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-full text-xs">{ticket.category}</span>
                )}
                {ticket.priority && (
                  <span className={`px-2 py-1 rounded-full border text-xs ${getPriorityBadge(ticket.priority)}`}>{ticket.priority}</span>
                )}
                {!ticket.category && !ticket.priority && (
                  <span className="text-slate-500 text-xs">No labels</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, valueColor = "text-slate-200" }) {
  return (
    <div className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/40">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`text-xs font-semibold ${valueColor}`}>{value}</span>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-slate-500 text-xs">{label}</span>
      {highlight ? (
        <span className={`px-2 py-0.5 rounded-md border text-xs font-medium w-fit ${highlight}`}>{value || "N/A"}</span>
      ) : (
        <span className="text-slate-200 text-xs font-medium">{value || "—"}</span>
      )}
    </div>
  );
}

// ─── ActivityPost ─────────────────────────────────────────────────────────────

function ActivityPost({ activity, onDelete, onReply, onPostComment, onDeleteComment, onActivityReaction, onCommentReaction, replyingTo, setReplyingTo, newPost, setNewPost, formatDate, getUserAvatar, currentUser, API_BASE }) {
  const [showComments, setShowComments] = useState(true);
  const [showReactions, setShowReactions] = useState(false);

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/40 p-3.5">
      <div className="flex justify-between items-start mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center overflow-hidden border border-blue-500/30 flex-shrink-0">
            {getUserAvatar(activity.userName, activity.profilePicture)}
          </div>
          <div>
            <p className="text-white text-sm font-medium leading-tight">{activity.userName}</p>
            <p className="text-slate-500 text-xs">{formatDate ? formatDate(activity.createdAt) : activity.createdAt}{activity.updatedAt && " · edited"}</p>
          </div>
        </div>
        {activity.userId === currentUser.userId && (
          <button onClick={() => onDelete(activity.activityId)} className="p-1 text-slate-500 hover:text-red-400 rounded transition-colors">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {activity.content && activity.content.trim() && (
        <p className="text-slate-200 text-sm mb-3 whitespace-pre-wrap leading-relaxed">{activity.content}</p>
      )}

      {activity.hasImage && (
        <div className="mb-3">
          <img src={`${API_BASE}/Activities/${activity.activityId}/image`} alt="Activity" className="max-h-60 rounded-lg border border-slate-700 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(`${API_BASE}/Activities/${activity.activityId}/image`, '_blank')} />
        </div>
      )}

      {activity.hasAttachment && (
        <div className="mb-3">
          <a href={`${API_BASE}/Activities/${activity.activityId}/attachment`} download={activity.fileName} className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/50 hover:border-blue-500/50 transition-colors">
            <FileText size={16} className="text-blue-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs text-slate-300 block truncate">{activity.fileName || 'Attachment'}</span>
              {activity.fileSize && <span className="text-xs text-slate-500">{(activity.fileSize / 1024).toFixed(2)} KB</span>}
            </div>
            <Download size={14} className="text-slate-400 flex-shrink-0" />
          </a>
        </div>
      )}

      {activity.reactionSummary?.totalReactions > 0 && (
        <div className="mb-2 flex items-center gap-1.5">
          <div className="flex -space-x-1">
            {Object.entries(activity.reactionSummary.reactionCounts || {}).map(([type, count]) => {
              const ReactionIcon = REACTION_TYPES[type]?.icon;
              return ReactionIcon ? (
                <div key={type} className={`w-5 h-5 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`} title={`${count} ${type}`}>
                  <ReactionIcon size={10} />
                </div>
              ) : null;
            })}
          </div>
          <span className="text-slate-400 text-xs">{activity.reactionSummary.totalReactions}</span>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t border-slate-700/30">
        <div className="relative">
          <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors ${activity.reactionSummary?.userReaction ? REACTION_TYPES[activity.reactionSummary.userReaction]?.color : ''}`}>
            {activity.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[activity.reactionSummary.userReaction]?.icon; return R ? <><R size={12} />{activity.reactionSummary.userReaction}</> : <><ThumbsUp size={12} />React</>; })() : <><ThumbsUp size={12} />React</>}
          </button>
          {showReactions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} />
              <div className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1.5 flex gap-1 z-20">
                {Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (
                  <button key={type} onClick={() => { onActivityReaction(activity.activityId, type); setShowReactions(false); }} className={`p-1.5 rounded-lg ${color} ${hoverColor} transition-colors ${activity.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`} title={type}>
                    <Icon size={14} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <button onClick={() => setShowComments(!showComments)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors">
          <MessageCircle size={12} />{activity.commentCount} {activity.commentCount === 1 ? "comment" : "comments"}
        </button>
        <button onClick={() => onReply(activity.activityId, null, activity.userName)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors">
          <Reply size={12} />Reply
        </button>
      </div>

      {replyingTo?.activityId === activity.activityId && !replyingTo.commentId && (
        <div className="mt-3 ml-10 bg-slate-900/50 rounded-lg border border-slate-700/50 p-2.5">
          <textarea value={newPost} onChange={(e) => setNewPost(e.target.value)} placeholder={`Reply to ${replyingTo.userName}...`} className="w-full bg-transparent text-white placeholder-slate-500 border-0 outline-none resize-none min-h-[50px] text-xs" />
          <div className="flex justify-end gap-2 mt-1.5">
            <button onClick={() => setReplyingTo(null)} className="px-3 py-1 text-slate-400 hover:text-white text-xs rounded transition-colors">Cancel</button>
            <button onClick={() => onPostComment(activity.activityId)} disabled={!newPost.trim()} className="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 text-white text-xs rounded transition-colors">Reply</button>
          </div>
        </div>
      )}

      {showComments && activity.comments?.length > 0 && (
        <div className="mt-3 space-y-2 ml-10">
          {activity.comments.map((comment) => (
            <Comment key={comment.commentId} comment={comment} activityId={activity.activityId} onDeleteComment={onDeleteComment} onReply={onReply} onPostComment={onPostComment} onCommentReaction={onCommentReaction} replyingTo={replyingTo} setReplyingTo={setReplyingTo} newPost={newPost} setNewPost={setNewPost} formatDate={formatDate} getUserAvatar={getUserAvatar} currentUser={currentUser} API_BASE={API_BASE} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Comment ──────────────────────────────────────────────────────────────────

function Comment({ comment, activityId, onDeleteComment, onReply, onPostComment, onCommentReaction, replyingTo, setReplyingTo, newPost, setNewPost, formatDate, getUserAvatar, currentUser, API_BASE }) {
  const [showReactions, setShowReactions] = useState(false);

  return (
    <div className="bg-slate-800/30 rounded-lg p-2.5">
      <div className="flex justify-between items-start mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center overflow-hidden border border-blue-500/30 flex-shrink-0">
            {getUserAvatar(comment.userName, comment.profilePicture)}
          </div>
          <div>
            <p className="text-white text-xs font-medium">{comment.userName}</p>
            <p className="text-slate-500 text-xs">{formatDate ? formatDate(comment.createdAt) : comment.createdAt}</p>
          </div>
        </div>
        {comment.userId === currentUser.userId && (
          <button onClick={() => onDeleteComment(comment.commentId)} className="p-0.5 text-slate-500 hover:text-red-400 rounded transition-colors"><Trash2 size={12} /></button>
        )}
      </div>

      <p className="text-slate-300 text-xs mb-2 ml-9">{comment.content}</p>

      {comment.reactionSummary?.totalReactions > 0 && (
        <div className="mb-1.5 ml-9 flex items-center gap-1">
          <div className="flex -space-x-1">
            {Object.entries(comment.reactionSummary.reactionCounts || {}).map(([type, count]) => {
              const R = REACTION_TYPES[type]?.icon;
              return R ? <div key={type} className={`w-4 h-4 rounded-full bg-slate-800 border border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`} title={`${count} ${type}`}><R size={8} /></div> : null;
            })}
          </div>
          <span className="text-slate-400 text-xs">{comment.reactionSummary.totalReactions}</span>
        </div>
      )}

      <div className="flex items-center gap-3 ml-9">
        <div className="relative">
          <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors ${comment.reactionSummary?.userReaction ? REACTION_TYPES[comment.reactionSummary.userReaction]?.color : ''}`}>
            {comment.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[comment.reactionSummary.userReaction]?.icon; return R ? <><R size={10} />{comment.reactionSummary.userReaction}</> : <><ThumbsUp size={10} />React</>; })() : <><ThumbsUp size={10} />React</>}
          </button>
          {showReactions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} />
              <div className="absolute bottom-full left-0 mb-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 flex gap-0.5 z-20">
                {Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (
                  <button key={type} onClick={() => { onCommentReaction(comment.commentId, type); setShowReactions(false); }} className={`p-1 rounded-lg ${color} ${hoverColor} transition-colors ${comment.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`} title={type}><Icon size={12} /></button>
                ))}
              </div>
            </>
          )}
        </div>
        <button onClick={() => onReply(activityId, comment.commentId, comment.userName)} className="text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors"><Reply size={10} />Reply</button>
      </div>

      {replyingTo?.commentId === comment.commentId && (
        <div className="mt-2 ml-9 bg-slate-700/30 rounded-lg border border-slate-600/50 p-2">
          <textarea value={newPost} onChange={(e) => setNewPost(e.target.value)} placeholder={`Reply to ${replyingTo.userName}...`} className="w-full bg-transparent text-white placeholder-slate-500 border-0 outline-none resize-none min-h-[40px] text-xs" />
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => setReplyingTo(null)} className="px-2 py-0.5 text-slate-400 hover:text-white text-xs rounded transition-colors">Cancel</button>
            <button onClick={() => onPostComment(activityId, comment.commentId)} disabled={!newPost.trim()} className="px-2 py-0.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 text-white text-xs rounded transition-colors">Reply</button>
          </div>
        </div>
      )}

      {comment.replies?.length > 0 && (
        <div className="mt-2 ml-9 space-y-1.5">
          {comment.replies.map((reply) => (
            <ReplyItem key={reply.commentId} reply={reply} onDeleteComment={onDeleteComment} onCommentReaction={onCommentReaction} formatDate={formatDate} getUserAvatar={getUserAvatar} currentUser={currentUser} API_BASE={API_BASE} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ReplyItem ────────────────────────────────────────────────────────────────

function ReplyItem({ reply, onDeleteComment, onCommentReaction, formatDate, getUserAvatar, currentUser, API_BASE }) {
  const [showReactions, setShowReactions] = useState(false);

  return (
    <div className="bg-slate-700/20 rounded-lg p-2">
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center overflow-hidden border border-green-500/30 flex-shrink-0">
            {getUserAvatar(reply.userName, reply.profilePicture)}
          </div>
          <div>
            <p className="text-white text-xs font-medium">{reply.userName}</p>
            <p className="text-slate-500 text-xs">{formatDate ? formatDate(reply.createdAt) : reply.createdAt}</p>
          </div>
        </div>
        {reply.userId === currentUser.userId && (
          <button onClick={() => onDeleteComment(reply.commentId)} className="p-0.5 text-slate-500 hover:text-red-400 rounded transition-colors"><Trash2 size={11} /></button>
        )}
      </div>

      <p className="text-slate-300 text-xs ml-8 mb-1">{reply.content}</p>

      {reply.reactionSummary?.totalReactions > 0 && (
        <div className="mb-1 ml-8 flex items-center gap-1">
          <div className="flex -space-x-1">
            {Object.entries(reply.reactionSummary.reactionCounts || {}).map(([type, count]) => {
              const R = REACTION_TYPES[type]?.icon;
              return R ? <div key={type} className={`w-4 h-4 rounded-full bg-slate-800 border border-slate-900 flex items-center justify-center ${REACTION_TYPES[type].color}`} title={`${count} ${type}`}><R size={7} /></div> : null;
            })}
          </div>
          <span className="text-slate-400 text-xs">{reply.reactionSummary.totalReactions}</span>
        </div>
      )}

      <div className="ml-8 relative inline-block">
        <button onClick={() => setShowReactions(!showReactions)} className={`text-slate-400 hover:text-blue-400 text-xs flex items-center gap-1 transition-colors ${reply.reactionSummary?.userReaction ? REACTION_TYPES[reply.reactionSummary.userReaction]?.color : ''}`}>
          {reply.reactionSummary?.userReaction ? (() => { const R = REACTION_TYPES[reply.reactionSummary.userReaction]?.icon; return R ? <><R size={10} />{reply.reactionSummary.userReaction}</> : <><ThumbsUp size={10} />React</>; })() : <><ThumbsUp size={10} />React</>}
        </button>
        {showReactions && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowReactions(false)} />
            <div className="absolute bottom-full left-0 mb-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 flex gap-0.5 z-20">
              {Object.entries(REACTION_TYPES).map(([type, { icon: Icon, color, hoverColor }]) => (
                <button key={type} onClick={() => { onCommentReaction(reply.commentId, type); setShowReactions(false); }} className={`p-1 rounded-lg ${color} ${hoverColor} transition-colors ${reply.reactionSummary?.userReaction === type ? 'bg-slate-700' : ''}`} title={type}><Icon size={11} /></button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}